import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  multiFactor,
  RecaptchaVerifier,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../theme';
import { getFriendlyAuthErrorMessage } from '../utils/authErrorMessages';

// HC-SECURITY-007 (Prompt #329) — Tammy's confirmed minimal scope: one page,
// two things only — basic profile fields, and MFA enrollment. No session
// management, no phone-number-change/unenroll flow, no broader settings.
// Reached via a small gear icon next to the SUPERADMIN badge in AppShell.js,
// not a full sidebar nav item.
// HC-SECURITY-008 (Prompt #345) — the change/unenroll flow flagged as
// out of scope above is now built, below. This is the allowBareUnenroll:
// false variant (per HC-SECURITY-008's own scoping, issue #73/TECH_SPEC.md
// §15.2) — only a combined "Change Phone Number" flow is offered, never a
// standalone "Turn Off MFA" button, since a super-admin account is never
// meant to be left with zero enrolled factors. When HC-SECURITY-009 ships
// employer-side MFA, web-admin's own port of this same mechanism is the
// allowBareUnenroll: true variant — a real UI difference, not just a flag,
// so it is a genuinely separate (if similar) build there, not this file.

// Fixed, non-PII label for the enrolled factor's Auth-level displayName.
// Deliberately NOT the actual phone number: enrolledFactors[i].phoneNumber
// is masked server-side by Firebase (e.g. "+*******1234"), but displayName
// is free text we control and is NOT masked — passing the raw phone number
// here would put unmasked PII in a field this same page (and the Auth user
// record generally) displays back out.
const MFA_FACTOR_DISPLAY_NAME = 'Primary phone';

const cardStyle = {
  backgroundColor: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: '8px',
  padding: '24px',
  marginBottom: '24px',
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: '600',
  color: COLORS.textSecondary,
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  backgroundColor: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  color: COLORS.textPrimary,
  padding: '10px 12px',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  marginBottom: '16px',
};

function primaryButtonStyle(disabled) {
  return {
    backgroundColor: disabled ? COLORS.border : COLORS.accent,
    color: '#1B1B1F',
    fontWeight: '600',
    fontSize: '14px',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function StatusLine({ status, message }) {
  if (!status) return null;
  return (
    <div style={{
      marginTop: '12px',
      fontSize: '13px',
      color: status === 'success' ? COLORS.success : COLORS.danger,
    }}>
      {message}
    </div>
  );
}

function formatEnrollmentTime(enrollmentTime) {
  if (!enrollmentTime) return null;
  const d = new Date(enrollmentTime);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function SecurityPage() {
  const { user } = useAuth();

  // ── Profile fields (first/last name, email) — sourced from and saved
  // back to the caller's own users/{uid} Firestore doc, the same root-level
  // firstName/lastName/email fields already used elsewhere in this schema
  // (see functions/src/functions/auth-functions.ts's deleteAccount scrub
  // payload for the canonical field shape). Only the Firestore copy of
  // email is edited here — this does NOT call Firebase Auth's updateEmail()
  // or change the account's actual sign-in address, matching the prompt's
  // literal scope ("saved back to the user's own Firestore users/{uid} doc").
  const [profileLoading, setProfileLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [loadedProfile, setLoadedProfile] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileStatus, setProfileStatus] = useState('');
  const [profileMessage, setProfileMessage] = useState('');

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, 'users', user.uid))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          const fn = data.firstName || '';
          const ln = data.lastName || '';
          const em = data.email || '';
          setFirstName(fn);
          setLastName(ln);
          setEmail(em);
          setLoadedProfile({ firstName: fn, lastName: ln, email: em });
        }
      })
      .finally(() => setProfileLoading(false));
  }, [user]);

  async function handleSaveProfile(e) {
    e.preventDefault();
    setProfileSaving(true);
    setProfileStatus('');
    setProfileMessage('');
    try {
      const updates = {};
      if (firstName.trim() !== (loadedProfile?.firstName ?? '')) updates.firstName = firstName.trim();
      if (lastName.trim() !== (loadedProfile?.lastName ?? '')) updates.lastName = lastName.trim();
      if (email.trim() !== (loadedProfile?.email ?? '')) updates.email = email.trim();
      if (Object.keys(updates).length > 0) {
        await updateDoc(doc(db, 'users', user.uid), updates);
        setLoadedProfile(prev => ({ ...prev, ...updates }));
      }
      setProfileStatus('success');
      setProfileMessage('Saved.');
      setTimeout(() => { setProfileStatus(''); setProfileMessage(''); }, 2500);
    } catch (_err) {
      setProfileStatus('error');
      setProfileMessage('Failed to save. Please try again.');
    } finally {
      setProfileSaving(false);
    }
  }

  // ── MFA enrollment ──────────────────────────────────────────────────────
  // NOT the login-completion path (that's LoginPage.js's resolver.resolveSignIn()
  // for a user who is already enrolled, mid-sign-in). This is first-time
  // enrollment on an already-signed-in session: multiFactor(user).getSession()
  // → RecaptchaVerifier → verifyPhoneNumber() → code entry →
  // multiFactor(user).enroll(assertion, displayName). Same internal SDK call
  // chain and same reCAPTCHA v2 fallback machinery Prompt #328 investigated
  // for the sign-in path (verified via direct source read) — including the
  // same authorizedDomains dependency, already fixed for this project's real
  // super-admin domains as of that prompt.
  const [mfaLoading, setMfaLoading] = useState(true);
  const [enrolledFactors, setEnrolledFactors] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');
  const recaptchaVerifierRef = useRef(null);

  // ── Change Phone Number (HC-SECURITY-008, Prompt #345) ──────────────────
  // Not a separate enrollment UI — unenrolling the old factor, on success,
  // simply causes refreshEnrolledFactors() to report 0 factors, which makes
  // the existing !verificationId branch below (the Send Code form) render
  // automatically. pendingSessionRevokeRef survives that transition so
  // handleEnroll's own success path (below, unmodified in its enroll logic)
  // knows to revoke sessions once the NEW factor finishes enrolling — a ref,
  // not state, since it doesn't need to trigger a render on its own.
  const [changeMode, setChangeMode] = useState(false);
  const [changePassword, setChangePassword] = useState('');
  const [changeBusy, setChangeBusy] = useState(false);
  const [changeError, setChangeError] = useState('');
  const pendingSessionRevokeRef = useRef(false);

  function handleStartChange() {
    setChangeMode(true);
    setChangePassword('');
    setChangeError('');
  }

  function handleCancelChange() {
    setChangeMode(false);
    setChangePassword('');
    setChangeError('');
    setChangeBusy(false);
  }

  async function handleConfirmChange(e) {
    e.preventDefault();
    setChangeError('');
    setChangeBusy(true);
    try {
      // Re-authentication mirrors web-admin's AccountSettingsPage.js
      // password-change pattern (EmailAuthProvider.credential +
      // reauthenticateWithCredential) — confirmed by direct read of that
      // file before matching it (HC-SECURITY-008 scoping, issue #73).
      const credential = EmailAuthProvider.credential(auth.currentUser.email, changePassword);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await multiFactor(auth.currentUser).unenroll(enrolledFactors[0]);
      pendingSessionRevokeRef.current = true;
      handleCancelChange();
      await refreshEnrolledFactors();
    } catch (err) {
      setChangeError(getFriendlyAuthErrorMessage(err));
      setChangeBusy(false);
    }
  }

  const refreshEnrolledFactors = useCallback(async () => {
    setMfaLoading(true);
    try {
      if (auth.currentUser) {
        // A factor enrolled in a different tab/session, or just enrolled by
        // this page itself, may not be reflected on the cached currentUser
        // object without a reload.
        await auth.currentUser.reload();
        setEnrolledFactors(multiFactor(auth.currentUser).enrolledFactors);
      }
    } catch (_err) {
      // Leave enrolledFactors as whatever it already was — not fatal, the
      // rest of the page (profile fields) still renders and works.
    } finally {
      setMfaLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshEnrolledFactors();
  }, [refreshEnrolledFactors]);

  function resetMfaEnrollState() {
    if (recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current.clear();
      recaptchaVerifierRef.current = null;
    }
    setPhoneNumber('');
    setVerificationId(null);
    setEnrollCode('');
    setMfaBusy(false);
    setMfaError('');
  }

  async function handleSendCode(e) {
    e.preventDefault();
    setMfaError('');
    setMfaBusy(true);
    try {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
      setVerificationId(null);
      const session = await multiFactor(auth.currentUser).getSession();
      const verifier = new RecaptchaVerifier(auth, 'security-mfa-recaptcha-container', { size: 'normal' });
      recaptchaVerifierRef.current = verifier;
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const id = await phoneAuthProvider.verifyPhoneNumber({ phoneNumber: phoneNumber.trim(), session }, verifier);
      setVerificationId(id);
    } catch (err) {
      setMfaError(getFriendlyAuthErrorMessage(err));
    } finally {
      setMfaBusy(false);
    }
  }

  async function handleEnroll(e) {
    e.preventDefault();
    if (!verificationId) return;
    setMfaError('');
    setMfaBusy(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, enrollCode.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await multiFactor(auth.currentUser).enroll(assertion, MFA_FACTOR_DISPLAY_NAME);
      resetMfaEnrollState();

      // HC-SECURITY-008 (Prompt #345) — if this enrollment is completing a
      // Change Phone Number flow (not a first-time enrollment),
      // revoke all other sessions now that the new factor is confirmed
      // working. Deliberately does NOT block/undo the enrollment itself if
      // this call fails — the phone-number change already succeeded and is
      // the more important guarantee; a revoke failure is surfaced but not
      // treated as the enrollment having failed.
      let sessionMessage = '';
      if (pendingSessionRevokeRef.current) {
        pendingSessionRevokeRef.current = false;
        try {
          await httpsCallable(functions, 'revokeMySessions')();
          // See revokeMySessions' own doc comment (functions/src/functions/
          // admin-functions.ts) and this prompt's report: Firebase Auth has
          // no mechanism to exclude the acting session from this revoke —
          // it revokes ALL sessions, this tab's own included. This tab
          // keeps working until its current ID token's natural expiry
          // (up to 1 hour), same as any other still-valid session would.
          sessionMessage = ' Other signed-in sessions have been signed out (this includes this browser, once its current session naturally expires).';
        } catch (revokeErr) {
          sessionMessage = ' Note: other sessions could not be automatically signed out — you may want to run the recovery script or try again.';
          // eslint-disable-next-line no-console
          console.error('revokeMySessions failed after phone number change:', revokeErr);
        }
      }

      setMfaSuccess(`Two-factor authentication enabled.${sessionMessage}`);
      await refreshEnrolledFactors();
      setTimeout(() => setMfaSuccess(''), sessionMessage ? 8000 : 4000);
    } catch (err) {
      setMfaError(getFriendlyAuthErrorMessage(err));
      setMfaBusy(false);
    }
  }

  const isEnrolled = enrolledFactors.length > 0;

  return (
    <div style={{ maxWidth: '560px' }}>

      {/* Profile fields */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', color: COLORS.textPrimary, marginTop: 0, marginBottom: '20px' }}>
          Profile
        </h2>
        {profileLoading ? (
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>Loading…</div>
        ) : (
          <form onSubmit={handleSaveProfile}>
            <label htmlFor="security-first-name" style={labelStyle}>First Name</label>
            <input
              id="security-first-name"
              type="text"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              style={inputStyle}
            />
            <label htmlFor="security-last-name" style={labelStyle}>Last Name</label>
            <input
              id="security-last-name"
              type="text"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              style={inputStyle}
            />
            <label htmlFor="security-email" style={labelStyle}>Email</label>
            <input
              id="security-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={inputStyle}
            />
            <button type="submit" disabled={profileSaving} style={primaryButtonStyle(profileSaving)}>
              {profileSaving ? 'Saving…' : 'Save'}
            </button>
            <StatusLine status={profileStatus} message={profileMessage} />
          </form>
        )}
      </div>

      {/* MFA */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', color: COLORS.textPrimary, marginTop: 0, marginBottom: '8px' }}>
          Two-Factor Authentication
        </h2>

        {mfaLoading ? (
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>Loading…</div>
        ) : isEnrolled && !changeMode ? (
          // HC-SECURITY-008 (Prompt #345): status + a Change Phone Number
          // action. Deliberately no standalone "Turn Off" button here — see
          // this file's own top-of-file note on the allowBareUnenroll:
          // false scope for super-admin.
          <div>
            <div style={{ color: COLORS.success, fontSize: '13px', fontWeight: '600', marginBottom: '8px' }}>
              ✓ Enabled
            </div>
            <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
              {enrolledFactors[0].phoneNumber}
            </div>
            {formatEnrollmentTime(enrolledFactors[0].enrollmentTime) && (
              <div style={{ color: COLORS.textMuted, fontSize: '12px', marginTop: '4px' }}>
                Enrolled {formatEnrollmentTime(enrolledFactors[0].enrollmentTime)}
              </div>
            )}
            <button
              type="button"
              onClick={handleStartChange}
              style={{ ...primaryButtonStyle(false), marginTop: '16px', backgroundColor: COLORS.surface, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
            >
              Change Phone Number
            </button>
          </div>
        ) : isEnrolled && changeMode ? (
          <form onSubmit={handleConfirmChange}>
            <p style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: 0, marginBottom: '16px' }}>
              Re-enter your password to continue. This clears your current phone number and other signed-in sessions will be signed out.
            </p>
            <label htmlFor="security-change-password" style={labelStyle}>Password</label>
            <input
              id="security-change-password"
              type="password"
              value={changePassword}
              onChange={e => setChangePassword(e.target.value)}
              style={inputStyle}
              autoFocus
              required
            />
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button type="submit" disabled={changeBusy || !changePassword} style={primaryButtonStyle(changeBusy || !changePassword)}>
                {changeBusy ? 'Verifying…' : 'Continue'}
              </button>
              <button
                type="button"
                onClick={handleCancelChange}
                style={{ background: 'none', border: 'none', color: COLORS.textSecondary, fontSize: '13px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
            <StatusLine status={changeError ? 'error' : ''} message={changeError} />
          </form>
        ) : !verificationId ? (
          <form onSubmit={handleSendCode}>
            <p style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: 0, marginBottom: '16px' }}>
              Add a phone number to receive a verification code at sign-in.
            </p>
            <label htmlFor="security-phone-number" style={labelStyle}>Phone Number</label>
            <input
              id="security-phone-number"
              type="tel"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
              placeholder="+15551234567"
              style={inputStyle}
              required
            />
            <div id="security-mfa-recaptcha-container" style={{ marginBottom: '16px' }} />
            <button type="submit" disabled={mfaBusy || !phoneNumber.trim()} style={primaryButtonStyle(mfaBusy || !phoneNumber.trim())}>
              {mfaBusy ? 'Sending…' : 'Send Code'}
            </button>
            <StatusLine status={mfaError ? 'error' : ''} message={mfaError} />
          </form>
        ) : (
          <form onSubmit={handleEnroll}>
            <p style={{ fontSize: '13px', color: COLORS.textSecondary, marginTop: 0, marginBottom: '16px' }}>
              Enter the verification code sent to your phone.
            </p>
            <label htmlFor="security-enroll-code" style={labelStyle}>Verification Code</label>
            <input
              id="security-enroll-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={enrollCode}
              onChange={e => setEnrollCode(e.target.value)}
              style={inputStyle}
              autoFocus
              required
            />
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button type="submit" disabled={mfaBusy || !enrollCode.trim()} style={primaryButtonStyle(mfaBusy || !enrollCode.trim())}>
                {mfaBusy ? 'Verifying…' : 'Enable Two-Factor Authentication'}
              </button>
              <button
                type="button"
                onClick={resetMfaEnrollState}
                style={{ background: 'none', border: 'none', color: COLORS.textSecondary, fontSize: '13px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
            <StatusLine status={mfaError ? 'error' : ''} message={mfaError} />
          </form>
        )}

        {mfaSuccess && <StatusLine status="success" message={mfaSuccess} />}
      </div>

      {/* Legal (Prompt #368) — real, connected pro.workrmatch.app domain,
          same one terms_acceptance_screen.dart's mobile link uses
          (Prompt #362) — not the dead bare apex. */}
      <div style={cardStyle}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', color: COLORS.textPrimary, marginTop: 0, marginBottom: '16px' }}>
          Legal
        </h2>
        <div style={{ display: 'flex', gap: '1.5rem' }}>
          <a
            href="https://pro.workrmatch.app/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.accent, fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}
          >
            Terms of Service
          </a>
          <a
            href="https://pro.workrmatch.app/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.accent, fontSize: '14px', fontWeight: '600', textDecoration: 'none' }}
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
}
